/**
 * Flash-highlight registry for newly created or just-changed records.
 *
 * Pages call `pushHighlight({ kind, ids })` after a successful mutation
 * (mark-paid, mark-collected, create-from-chat, new-payment, …) and the
 * list rows subscribe via `useHighlight(kind, id)` to add a CSS pulse
 * class for ~TTL_MS.
 *
 * Why a tiny pub/sub instead of React-Query state? The highlight is a
 * VIEW concern that's orthogonal to data — we don't want to refetch or
 * mutate the cache shape just to surface "this row is fresh". Also the
 * list query refetches asynchronously after invalidation, so the row
 * may render a moment later than the mutation completes — the registry
 * keeps the highlight pending until the row actually appears.
 *
 * IMPORTANT: state is module-scoped in-memory — survives SPA route
 * changes but is WIPED on a full page reload. That's fine in practice
 * because the toast 'Ver' action uses react-router `navigate()` (SPA),
 * not `window.location.href`. Don't replace those navigation paths with
 * full reloads or the highlight will disappear before the user lands
 * on the destination page.
 *
 * Kinds in use today:
 *   - "movement"                          → /history row by id
 *   - "movement_src_payment"              → /history row by source_payment_id
 *   - "movement_src_receivable"           → /history row by source_receivable_id
 *   - "payment"                           → /payments row by id
 *   - "receivable"                        → /receivables row by id
 */
import { useEffect, useState } from "react";

// 4s gives users a comfortable window to: (1) see the toast, (2) click
// "Ver", (3) land on /history, (4) catch the row still glowing. Bumped
// from 2.8s after observing tight margins on slow networks.
const TTL_MS = 4000;

/** kind → Map<id, expiresAtEpochMs> */
const registry = new Map();
const listeners = new Set();

function notify() {
  for (const l of listeners) l();
}

function prune(now = Date.now()) {
  let changed = false;
  for (const [kind, idMap] of registry) {
    for (const [id, exp] of idMap) {
      if (exp <= now) {
        idMap.delete(id);
        changed = true;
      }
    }
    if (idMap.size === 0) registry.delete(kind);
  }
  return changed;
}

/**
 * Mark one or more ids as recently changed for `kind`. They'll cause
 * matching rows to flash-pulse for ~TTL_MS.
 *
 * `ids` may be a single value or an array. Falsy entries are ignored
 * (e.g. payments paid via API don't always return a movement id, so
 * passing it as undefined is a no-op).
 */
export function pushHighlight(kind, ids) {
  if (!kind) return;
  const arr = Array.isArray(ids) ? ids : [ids];
  const expires = Date.now() + TTL_MS;
  let idMap = registry.get(kind);
  if (!idMap) {
    idMap = new Map();
    registry.set(kind, idMap);
  }
  let added = false;
  for (const raw of arr) {
    if (!raw) continue;
    idMap.set(String(raw), expires);
    added = true;
  }
  if (added) {
    notify();
    setTimeout(() => {
      prune();
      notify();
    }, TTL_MS + 50);
  }
}

/**
 * `true` while at least one of the passed ids has an unexpired
 * highlight under `kind`. Subscribes to registry changes.
 */
export function useHighlight(kind, ...ids) {
  const [, force] = useState(0);
  useEffect(() => {
    const cb = () => force((n) => n + 1);
    listeners.add(cb);
    return () => listeners.delete(cb);
  }, []);
  if (!kind) return false;
  const idMap = registry.get(kind);
  if (!idMap) return false;
  const now = Date.now();
  for (const raw of ids) {
    if (!raw) continue;
    const exp = idMap.get(String(raw));
    if (exp && exp > now) return true;
  }
  return false;
}

/**
 * Convenience for `<MovementRow />`: highlights when the movement
 * itself is fresh, OR when it was just produced by a pay/collect
 * action (source_payment_id / source_receivable_id match).
 */
export function useMovementHighlight(m) {
  // All three hooks must run unconditionally per render (rules-of-hooks).
  const byId = useHighlight("movement", m?.id);
  const bySrcPay = useHighlight("movement_src_payment", m?.source_payment_id);
  const bySrcRecv = useHighlight("movement_src_receivable", m?.source_receivable_id);
  return byId || bySrcPay || bySrcRecv;
}
